import StudentEdit from "@/controllers/custom/student/student-edit";
import axios from "axios";
import store from "@/store";
import {
  required,
  maxLength,
  minLength,
  email,
  numeric,
  not,
  sameAs,
} from "vuelidate/lib/validators";

export default {
  extends: StudentEdit,
  name: "StudentRestore",

  validations: {
    ...StudentEdit.validations,
    parentData: {
      guardian1_name: {
        required,
        maxLength: maxLength(50),
      },
      guardian1_job: {
        maxLength: maxLength(200),
      },
      guardian2_name: {
        maxLength: maxLength(50),
      },
      guardian2_job: {
        maxLength: maxLength(200),
      },
      email: {
        maxLength: maxLength(50),
        email,
      },
      phone1: {
        required,
        numeric,
        minLength: minLength(8),
        maxLength: maxLength(11),
      },
      phone2: {
        numeric,
        minLength: minLength(8),
        maxLength: maxLength(11),
        notSameAs: not(sameAs("phone1")),
      },
      address: {
        required,
        maxLength: maxLength(1000),
      },
    },
  },

  methods: {
    createEmptyParentData(parentNo = "") {
      return {
        parent_no: parentNo,
        guardian1_name: "",
        guardian1_job: "",
        guardian2_name: "",
        guardian2_job: "",
        guardian1_nrc_number: "",
        guardian2_nrc_number: "",
        email: "",
        phone1: "",
        phone2: "",
        address: "",
      };
    },

    async fetchNextParentNumber() {
      const response = await axios.get("/student/generate-parent-number");
      return response.data.parent_no || "";
    },

    async addParentInfo() {
      let parentNo = "";
      this.isLoading = true;

      try {
        parentNo = await this.fetchNextParentNumber();
      } catch (error) {
        if (error.response && error.response.status == 401) {
          this.$store.commit("logout");
          window.location.replace("login");
          return;
        }

        this.$notification.error("Unable to generate parent number.", {
          timer: 3,
          position: "bottomCenter",
        });
      } finally {
        this.isLoading = false;
      }

      this.parentData = this.createEmptyParentData(parentNo);
      this.clearValidationErrors();
      this.$nextTick(() => this.$v.parentData.$reset());
    },

    cancelParentInfo() {
      this.parentData = {};
      this.clearValidationErrors();
      this.$nextTick(() => this.$v.parentData.$reset());
    },

    showValidationError(message) {
      this.$notification.error(message, {
        timer: 3,
        position: "bottomCenter",
      });
    },

    async getStudentEdit() {
      try {
        const request = {
          params: {
            campus_id: store.state.user.campus_id,
          },
        };
        const response = await axios.get("/student/leavedProfile/" + this.$route.params.id, request);
        if (!response.data) {
          return;
        }

        const studentInfo = response.data;

        if (!studentInfo || !studentInfo.deleted_at) {
          return;
        }

        const academicYearId =
          studentInfo.trashed_class?.trashed_campus_grade?.acd_year_id ||
          studentInfo.classes?.[0]?.acd_year_id ||
          0;

        this.studentData = {
          ...(studentInfo.trashed_student || {}),
          additionalItems: studentInfo.additionalItems || [],
          acd_year_id: academicYearId,
          ferry_status: studentInfo.trashed_student?.ferry_status ?? 0,
        };
        this.parentData = studentInfo.trashed_student?.trashed_parent || {};
        this.student_image = this.studentData.profile_path || "";

        if (Array.isArray(studentInfo.classes) && studentInfo.classes.length > 0) {
          this.mainClass = studentInfo.classes[0] || {};
          this.classes = studentInfo.classes.slice(1);
        } else {
          this.mainClass = {
            grade_id: 0,
            class_id: 0,
            annual_fee: 0,
            monthly_fee: 0,
            section: "",
          };
          this.classes = [];
        }

        this.selectedReligion = this.religion = this.studentData.religion || 0;
        this.selectAcdYear = academicYearId;

        if (this.selectAcdYear) {
          await this.getGradeName(this.selectAcdYear);
        }

        const mainGradeId = this.mainClass?.grade_id || 0;
        const mainClassId = this.mainClass?.class_id || 0;

        if (mainGradeId > 0) {
          const sectionRequest = {
            params: {
              campus_id: store.state.user.campus_id,
              academic_year: this.selectAcdYear,
            },
          };
          this.getSection(mainGradeId, sectionRequest, mainClassId, "main");
        }

      } catch (error) {
        if (error.response && error.response.status == 401) {
          this.$store.commit("logout");
          window.location.replace("login");
        }
        console.log("Student Restore Detail API Error", error);
      } finally {
        this.isLoading = false;
      }
    },

    confirmRestore() {
      if (this.disabled) return;
      this.submitted = true;
      this.clearValidationErrors();
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.restoreStudent();
    },

    restoreStudent() {
      if (this.disabled) return;

      this.disabled = true;
      this.isLoading = true;
      this.clearValidationErrors();
      const combinedClasses = [this.mainClass, ...this.classes].filter(
        (item, index, self) =>
          item && self.findIndex((t) => t?.id === item.id) === index
      );
      const hasParentData = this.parentData && Object.keys(this.parentData).length !== 0;

      axios
        .post("/student/restore/" + this.$route.params.id, {
          parent_id: this.parentData?.id || null,
          name: this.studentData.name,
          student_image: this.student_image,
          student_image_old: this.studentData.profile_path,
          c_student_no: this.studentData.c_student_no,
          dob: this.studentData.dob,
          entry_no: this.studentData.entry_no,
          gender: this.studentData.gender,
          result: this.studentData.result,
          nationality: this.studentData.nationality,
          religion: this.religion,
          weight: this.studentData.weight,
          phone1: this.studentData.phone1,
          phone2: this.studentData.phone2,
          blood: this.studentData.blood,
          height: this.studentData.height,
          sibling: this.studentData.sibling,
          hobby: this.studentData.hobby,
          allergic_food: this.studentData.allergic_food,
          previous_school: this.studentData.previous_school,
          special_topic: this.studentData.special_topic,
          campus_id: store.state.user.campus_id,
          ferry_status: this.studentData.ferry_status,
          updated_id: store.state.user.id,
          academic_year: this.selectAcdYear,
          classes: combinedClasses,
          parent_data: hasParentData
            ? {
                guardian1_name: this.parentData.guardian1_name,
                guardian1_job: this.parentData.guardian1_job,
                guardian2_name: this.parentData.guardian2_name,
                guardian2_job: this.parentData.guardian2_job,
                guardian1_nrc_number: this.parentData.guardian1_nrc_number,
                guardian2_nrc_number: this.parentData.guardian2_nrc_number,
                email: this.parentData.email,
                phone1: this.parentData.phone1,
                phone2: this.parentData.phone2,
                address: this.parentData.address,
              }
            : null,
        })
        .then((response) => {
          if (response.data.success === true) {
            this.$notification.success(response.data.message || "Student Restored Successfully", {
              timer: 3,
              position: "bottomCenter",
            });
            this.showList();
            return;
          }

          this.disabled = false;
          this.$notification.error(response.data.message || "Unable to restore student.", {
            timer: 3,
            position: "bottomCenter",
          });
        })
        .catch((error) => {
          const response = error.response;

          if (response?.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
            return;
          }

          this.disabled = false;

          if (response?.status == 422 && response?.data?.errors) {
            this.printValidationError(response);
            return;
          }

          this.$notification.error(response?.data?.message || "Unable to restore student.", {
            timer: 3,
            position: "bottomCenter",
          });
          console.log("Student Restore API Error", error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
